/**
 * Acionador persistente do Assistente Comercial.
 *
 * Aparece em TODA página autenticada, menos no painel inicial — lá o assistente
 * já está em destaque no conteúdo, e um botão flutuante sobre ele seria a mesma
 * coisa oferecida duas vezes na mesma tela.
 *
 * O PAINEL NÃO SUBSTITUI A TELA
 *
 * Ele entra pela lateral e empurra nada: a pessoa continua vendo o Kanban, a
 * lista de empresas, a ficha que estava lendo. Isso é requisito e é o que
 * distingue um assistente de um chatbot em página própria — pedir "cria um
 * follow-up para esta empresa" só faz sentido com a empresa à vista.
 *
 * No Kanban isso importa mais do que nas outras telas: o quadro é horizontal e
 * ficaria inutilizável se o painel cobrisse metade dele. Por isso o painel é
 * estreito, tem largura fixa e, em telas grandes, divide espaço em vez de
 * sobrepor.
 *
 * TECLADO
 *
 * Abre e fecha por teclado, fecha com Esc, e o foco entra no painel ao abrir e
 * volta a quem o abriu ao fechar. Abaixo de lg o painel cobre a tela e prende o
 * foco (é modal); acima, divide espaço e não prende. Um atalho que só funciona
 * com mouse exclui quem opera o CRM no teclado o dia inteiro — que é
 * justamente quem mais usaria.
 */

import { useEffect, useRef, useState, type KeyboardEvent as EventoDeTecla } from 'react';
import { useLocation } from 'react-router-dom';
import { IconSpark } from './ui';
import { Assistente } from './Assistente';
import { useAssistente } from '../lib/assistente';
import { t } from '../lib/i18n';

/** A partir daqui (lg) o painel divide espaço com a tela; abaixo, cobre a tela. */
const DIVIDE_ESPACO = '(min-width: 1024px)';

const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * O painel é MODAL quando cobre a tela (abaixo de lg): foco preso nele, o resto
 * inerte. Em tela grande ele divide espaço, e a tela de trás continua operável.
 */
function usePainelCobreATela(): boolean {
  const consultar = (): boolean =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : !window.matchMedia(DIVIDE_ESPACO).matches;
  const [cobre, setCobre] = useState(consultar);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia(DIVIDE_ESPACO);
    const aoMudar = (): void => setCobre(!consulta.matches);
    consulta.addEventListener?.('change', aoMudar);
    return () => consulta.removeEventListener?.('change', aoMudar);
  }, []);
  return cobre;
}

export function AssistenteDock(): JSX.Element | null {
  const { painelAberto, abrirPainel, fecharPainel, turnos } = useAssistente();
  const location = useLocation();
  const acionadorRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);
  /** Quem tinha o foco quando o painel abriu: é para lá que ele volta. */
  const origemDoFoco = useRef<HTMLElement | null>(null);
  /** O painel já esteve aberto? Sem isto, o acionador roubava o foco no carregamento. */
  const jaAbriu = useRef(false);
  const modal = usePainelCobreATela();

  // No painel inicial o assistente já está no conteúdo principal.
  const noPainelInicial = location.pathname === '/';

  // Abrir: o foco vai para o campo — UMA vez, na transição. Quem abriu com
  // teclado precisa chegar ao campo sem tabular pela página de trás.
  useEffect(() => {
    if (!painelAberto) return;
    jaAbriu.current = true;
    painelRef.current?.querySelector<HTMLElement>('textarea')?.focus();
  }, [painelAberto]);

  // Fechar: o foco volta para quem abriu (ou para o acionador). Só depois de
  // uma abertura — no carregamento da página ninguém pediu foco.
  useEffect(() => {
    if (painelAberto || !jaAbriu.current) return;
    const origem = origemDoFoco.current;
    origemDoFoco.current = null;
    if (origem !== null && origem.isConnected && origem !== document.body) {
      origem.focus({ preventScroll: true });
    } else {
      acionadorRef.current?.focus({ preventScroll: true });
    }
  }, [painelAberto]);

  if (noPainelInicial) return null;

  const abrir = (): void => {
    origemDoFoco.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    abrirPainel();
  };

  /**
   * Teclado DENTRO do painel. Esc fecha. Tab circula entre os elementos do
   * painel quando ele cobre a tela — sem isto, o foco escapava para a página
   * escondida atrás do véu, e quem usa teclado ou leitor de tela se perdia.
   */
  const aoTeclar = (e: EventoDeTecla<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      fecharPainel();
      return;
    }
    if (e.key !== 'Tab' || !modal || painelRef.current === null) return;
    // Fora: o que está escondido por atributo. Não por medida de layout
    // (`offsetParent`): ela depende do motor de renderização, e o filtro não
    // pode mudar de resultado conforme onde a tela é desenhada.
    const focaveis = [...painelRef.current.querySelectorAll<HTMLElement>(FOCAVEIS)].filter(
      (el) => el.closest('[hidden], [aria-hidden="true"], [inert]') === null,
    );
    if (focaveis.length === 0) return;
    const primeiro = focaveis[0]!;
    const ultimo = focaveis[focaveis.length - 1]!;
    if (e.shiftKey && document.activeElement === primeiro) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primeiro.focus();
    }
  };

  return (
    <>
      {!painelAberto && (
        <button
          ref={acionadorRef}
          type="button"
          onClick={abrir}
          data-acionador-do-assistente=""
          /*
           * POSIÇÃO (CERES-IA-002). O acionador ficava em `bottom-4` e, no
           * celular, caía em cima da navegação inferior — e da faixa de cookies.
           * As alturas das duas vêm de variáveis (`--altura-nav-inferior`, do
           * AppShell; `--altura-banner-cookies`, da própria faixa): o botão sobe
           * o quanto elas ocupam.
           */
          // Estilo em linha, e não classe: vence a regra antiga de `styles.css`
          // (`bottom: 9rem` com a faixa visível), que não conhecia a navegação.
          style={{ bottom: 'calc(var(--altura-nav-inferior, 0px) + var(--altura-banner-cookies, 0px) + 1rem)' }}
          className="fixed right-4 z-30 flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-2.5 text-xs font-medium text-ink shadow-pop transition hover:border-brand-300 hover:text-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          aria-label={t('assistente.abrir')}
          aria-haspopup="dialog"
        >
          <span className="flex h-5 w-5 items-center justify-center rounded bg-brand-100 text-brand-700">
            <IconSpark className="h-3.5 w-3.5" />
          </span>
          {/* O nome aparece também no celular (CERES-IA-FECHAMENTO-004): só o
              ícone não dizia o que o botão faz, e quem procurava "Assistente"
              não o encontrava. */}
          <span>{t('assistente.nomeCurto')}</span>
          {turnos.length > 0 && (
            <span className="h-1.5 w-1.5 rounded-full bg-brand-600" aria-hidden="true" />
          )}
          {turnos.length > 0 && <span className="sr-only">{t('assistente.conversaEmAndamento')}</span>}
        </button>
      )}

      {painelAberto && (
        <>
          {/* Véu apenas abaixo de lg: em tela grande o painel divide espaço e a
              tela de trás continua operável, que é o ponto. */}
          <div
            className="fixed inset-0 z-30 bg-surface-overlay lg:hidden"
            onClick={fecharPainel}
            aria-hidden="true"
          />
          <div
            ref={painelRef}
            role="dialog"
            aria-label={t('assistente.nome')}
            aria-modal={modal ? 'true' : 'false'}
            onKeyDown={aoTeclar}
            className="fixed inset-y-0 right-0 z-40 flex w-full max-w-sm flex-col border-l border-line bg-surface shadow-pop sm:w-96"
          >
            <Assistente variante="lateral" aoFechar={fecharPainel} />
          </div>
        </>
      )}
    </>
  );
}
